import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquareQuote, Loader2 } from "lucide-react";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";
import Crest from "./Crest";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GuestbookPage() {
  useReveal();
  const { c } = useSite();
  const g = c.guestbook;
  const { toast } = useToast();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", location: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/guestbook`);
      setEntries(res.data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast({ title: g.needFields });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/guestbook`, form);
      toast({ title: g.successTitle, description: g.successDesc });
      setForm({ name: "", location: "", message: "" });
    } catch (e) {
      toast({ title: "Error", description: "Could not submit. Please try again." });
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-parchment/30 focus:border-gold outline-none py-3 text-xl font-display text-parchment placeholder:text-parchment/35 transition-colors";

  return (
    <div className="bg-ink">
      <section className="pt-36 sm:pt-44 pb-16 border-b border-[var(--c-border)]">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-3 mb-6 text-gold">
            <MessageSquareQuote className="w-4 h-4" />
            <span className="eyebrow">{g.label}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-light leading-[0.98] text-parchment max-w-3xl">{g.title}</h1>
          <p className="mt-6 text-lg sm:text-xl leading-relaxed text-parchment/65 font-light max-w-2xl">{g.intro}</p>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-12">
          {/* form */}
          <form onSubmit={submit} className="lg:col-span-2 flex flex-col gap-5">
            <input className={inputCls} placeholder={g.namePlaceholder} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input className={inputCls} placeholder={g.placePlaceholder} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            <textarea className={`${inputCls} resize-none`} rows={4} placeholder={g.messagePlaceholder} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button type="submit" disabled={sending} className="self-start mt-2 inline-flex items-center gap-2 font-label text-[0.72rem] uppercase tracking-[0.22em] text-ink bg-gold px-9 py-4 hover:opacity-90 transition-opacity disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {g.submit}
            </button>
            <p className="text-sm text-parchment/45 font-light italic mt-1">{g.reviewNote}</p>
          </form>

          {/* entries */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="flex items-center gap-2 text-stone2"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
            ) : entries.length === 0 ? (
              <div className="flex flex-col items-center text-center py-16">
                <Crest size={48} className="opacity-40" />
                <p className="mt-6 font-display italic text-2xl text-parchment/40">{g.emptyText}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {entries.map((en) => (
                  <div key={en.id} className="reveal in-view border border-[var(--c-border)] bg-ink2 p-6">
                    <p className="font-display text-xl text-parchment leading-relaxed">“{en.message}”</p>
                    <div className="mt-3 flex items-center gap-2 font-label text-[0.62rem] uppercase tracking-[0.16em] text-gold">
                      {en.name}{en.location ? <span className="text-stone2">· {en.location}</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
